export interface PortGroupSuggestSource {
  id: string;
  name: string;
  region?: string;
  ports: string[];
  port_names?: Record<string, string>;
}

export const PORT_GROUP_VALUE_PREFIX = "pg:";

export function isPortGroupValue(value: string): boolean {
  return value.startsWith(PORT_GROUP_VALUE_PREFIX);
}

export function portGroupIdFromValue(value: string): string {
  return value.slice(PORT_GROUP_VALUE_PREFIX.length);
}

export function portGroupValue(id: string): string {
  return `${PORT_GROUP_VALUE_PREFIX}${id}`;
}

/** Multi-port allocation groups (e.g. China, Israel) — not wizard ad-hoc singles. */
export function isAllocationCountryPortGroup(
  pg: PortGroupSuggestSource,
): boolean {
  if (pg.id.startsWith("adhoc-")) return false;
  if (pg.region?.trim() === "Single Port") return false;
  return pg.ports.length > 1;
}

function portListSummary(pg: PortGroupSuggestSource, max = 4): string {
  const labels = pg.ports.map((locode) => {
    const named = pg.port_names?.[locode];
    if (named) return named.replace(` (${locode})`, "").trim();
    return locode;
  });
  if (labels.length <= max) return labels.join(" · ");
  return `${labels.slice(0, max).join(" · ")} +${labels.length - max} more`;
}

export function portGroupSuggestLabel(pg: PortGroupSuggestSource): {
  title: string;
  subtitle: string;
} {
  const region = pg.region?.trim();
  return {
    title: pg.name,
    subtitle: region
      ? `${region} — ${portListSummary(pg)}`
      : portListSummary(pg),
  };
}

/** Match allocation / wizard port groups against a search query. */
export function filterAllocationPortGroups<T extends PortGroupSuggestSource>(
  groups: T[],
  query: string,
  limit = 8,
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  return groups
    .filter((pg) => isAllocationCountryPortGroup(pg))
    .filter((pg) => {
      if (pg.name.toLowerCase().includes(q)) return true;
      if (pg.region?.toLowerCase().includes(q)) return true;
      for (const locode of pg.ports) {
        if (locode.toLowerCase().includes(q)) return true;
        const label = pg.port_names?.[locode];
        if (label?.toLowerCase().includes(q)) return true;
      }
      return false;
    })
    .slice(0, limit);
}

/** Country-scale buckets (e.g. China with 90+ ports) — not offered in Kai `@port`. */
export const KAI_PORT_GROUP_MAX_PORTS = 15;

/**
 * Trade-lane port groups for Kai `@port`: match only the **group name** (or
 * region label), never a member port name — so typing "Qingdao" does not
 * surface "China (94 ports)". Skips country-scale buckets.
 */
export function filterAllocationPortGroupsForKai<T extends PortGroupSuggestSource>(
  groups: T[],
  query: string,
  limit = 2,
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  return groups
    .filter((pg) => isAllocationCountryPortGroup(pg))
    .filter((pg) => pg.ports.length <= KAI_PORT_GROUP_MAX_PORTS)
    .filter((pg) => {
      if (pg.name.trim().toLowerCase().includes(q)) return true;
      const region = pg.region?.trim().toLowerCase();
      return Boolean(region && region.includes(q));
    })
    .slice(0, limit);
}
